import React from "react";

interface ChartPreviewIconProps {
  componentType: string;
  configKey: string;
  chartType?: string;
  valueFormat?: string;
}

const ChartPreviewIcon: React.FC<ChartPreviewIconProps> = ({
  componentType,
  configKey,
  chartType,
  valueFormat,
}) => {
  const commonProps = {
    width: "100%",
    height: "60",
    viewBox: "0 0 200 60",
    className: "card-chart",
  };

  // Metric previews
  const renderMetricPreview = (format: string) => {
    switch (format) {
      case "number":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="numGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#1E88E5" />
                <stop offset="100%" stopColor="#42A5F5" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="70"
              height="24"
              fill="url(#numGrad)"
              rx="8"
            />
            <text
              x="45"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              2,458
            </text>
            <polyline
              points="95,38 115,30 130,34 145,24 160,30"
              fill="none"
              stroke="#43A047"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="145" cy="24" r="3" fill="#43A047" />
          </svg>
        );

      case "duration":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="durGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3949AB" />
                <stop offset="100%" stopColor="#5C6BC0" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="90"
              height="24"
              fill="url(#durGrad)"
              rx="8"
            />
            <text
              x="55"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              3h 42m
            </text>
            <circle
              cx="120"
              cy="30"
              r="9"
              stroke="#43A047"
              strokeWidth="2"
              fill="none"
            />
            <line
              x1="120"
              y1="30"
              x2="120"
              y2="25"
              stroke="#43A047"
              strokeWidth="2"
            />
            <line
              x1="120"
              y1="30"
              x2="125"
              y2="30"
              stroke="#43A047"
              strokeWidth="2"
            />
          </svg>
        );

      case "avg-duration":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="avgGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#8E24AA" />
                <stop offset="100%" stopColor="#BA68C8" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="100"
              height="24"
              fill="url(#avgGrad)"
              rx="8"
            />
            <text
              x="60"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              1h 12m 35s
            </text>
            <path
              d="M120 40 Q135 25 150 30 Q165 35 180 22"
              fill="none"
              stroke="#43A047"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="180" cy="22" r="3" fill="#43A047" />
          </svg>
        );

      case "percentage":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="percGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#00C853" />
                <stop offset="100%" stopColor="#69F0AE" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="80"
              height="24"
              fill="url(#percGrad)"
              rx="8"
            />
            <text
              x="50"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              87%
            </text>
            <circle
              cx="120"
              cy="30"
              r="10"
              stroke="#E0E0E0"
              strokeWidth="3"
              fill="none"
            />
            <circle
              cx="120"
              cy="30"
              r="10"
              stroke="#43A047"
              strokeWidth="3"
              fill="none"
              strokeDasharray="62.8"
              strokeDashoffset="10"
              strokeLinecap="round"
              transform="rotate(-90 120 30)"
            />
            <circle cx="120" cy="30" r="2" fill="#43A047" />
          </svg>
        );

      case "time":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="timeGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#00897B" />
                <stop offset="100%" stopColor="#4DB6AC" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="90"
              height="24"
              fill="url(#timeGrad)"
              rx="8"
            />
            <text
              x="55"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              10:45 AM
            </text>
            <circle
              cx="125"
              cy="30"
              r="9"
              stroke="#43A047"
              strokeWidth="2"
              fill="none"
            />
            <line
              x1="125"
              y1="30"
              x2="125"
              y2="24"
              stroke="#43A047"
              strokeWidth="2"
            />
            <line
              x1="125"
              y1="30"
              x2="130"
              y2="30"
              stroke="#43A047"
              strokeWidth="2"
            />
          </svg>
        );

      case "currency":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="currGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F9A825" />
                <stop offset="100%" stopColor="#FFD54F" />
              </linearGradient>
            </defs>
            <rect
              x="10"
              y="18"
              width="95"
              height="24"
              fill="url(#currGrad)"
              rx="8"
            />
            <text
              x="57"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              ₹12,560
            </text>
            <polyline
              points="110,38 125,30 140,34 155,26"
              fill="none"
              stroke="#43A047"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="155" cy="26" r="3" fill="#43A047" />
          </svg>
        );

      default:
        return (
          <svg {...commonProps}>
            <rect x="10" y="18" width="70" height="24" fill="#1E88E5" rx="8" />
            <text
              x="45"
              y="35"
              fill="white"
              fontSize="13"
              textAnchor="middle"
              fontWeight="600">
              2,458
            </text>
            <polyline
              points="100,40 120,28 140,32 160,18 180,25"
              fill="none"
              stroke="#43A047"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="160" cy="18" r="3" fill="#43A047" />
          </svg>
        );
    }
  };

  // Chart type previews
  const renderChartPreview = (type: string) => {
    switch (type) {
      case "pie":
        return (
          <svg {...commonProps}>
            <circle cx="100" cy="30" r="22" fill="#E3F2FD" />
            <path d="M100,30 L100,8 A22,22 0 0,1 122,45 Z" fill="#1E88E5" />
            <path d="M100,30 L122,45 A22,22 0 0,1 80,45 Z" fill="#FDD835" />
            <path d="M100,30 L80,45 A22,22 0 0,1 100,8 Z" fill="#43A047" />
          </svg>
        );

      case "donut":
        return (
          <svg {...commonProps}>
            <circle cx="100" cy="30" r="22" fill="#E3F2FD" />
            <path d="M100,30 L100,8 A22,22 0 0,1 122,45 Z" fill="#1E88E5" />
            <path d="M100,30 L122,45 A22,22 0 0,1 80,45 Z" fill="#FDD835" />
            <path d="M100,30 L80,45 A22,22 0 0,1 100,8 Z" fill="#43A047" />
            <circle cx="100" cy="30" r="10" fill="white" />
          </svg>
        );

      case "line":
        return (
          <svg {...commonProps}>
            <polyline
              points="10,40 40,25 70,35 100,18 130,30 160,15 190,25"
              fill="none"
              stroke="#1E88E5"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx="100" cy="18" r="3" fill="#1E88E5" />
            <circle cx="160" cy="15" r="3" fill="#1E88E5" />
          </svg>
        );

      case "area":
        return (
          <svg {...commonProps}>
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#64B5F6" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#64B5F6" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <path
              d="M10,45 L40,30 L70,35 L100,20 L130,25 L160,15 L190,25 L190,45 Z"
              fill="url(#areaGradient)"
            />
            <polyline
              points="10,45 40,30 70,35 100,20 130,25 160,15 190,25"
              fill="none"
              stroke="#1E88E5"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );

      case "bar":
        return (
          <svg {...commonProps}>
            <rect x="20" y="25" width="20" height="25" fill="#90CAF9" rx="3" />
            <rect x="55" y="10" width="20" height="40" fill="#42A5F5" rx="3" />
            <rect x="90" y="20" width="20" height="30" fill="#1E88E5" rx="3" />
            <rect x="125" y="15" width="20" height="35" fill="#1565C0" rx="3" />
          </svg>
        );

      case "chart":
        return (
          <svg {...commonProps}>
            <rect x="20" y="40" width="20" height="10" fill="#90CAF9" rx="3" />
            <rect x="50" y="30" width="20" height="20" fill="#42A5F5" rx="3" />
            <rect x="80" y="20" width="20" height="30" fill="#1E88E5" rx="3" />
            <rect x="110" y="35" width="20" height="15" fill="#1565C0" rx="3" />
          </svg>
        );

      case "funnel":
        return (
          <svg
            {...commonProps}
            viewBox="0 0 200 60"
            preserveAspectRatio="xMidYMid meet">
            {/* Top (largest) */}
            <path d="M20,10 L180,10 L150,26 L50,26 Z" fill="#1E88E5" />
            <rect
              x="20"
              y="26"
              width="160"
              height="1"
              fill="rgba(255,255,255,0.15)"
            />
            {/* Second */}
            <path d="M40,28 L160,28 L140,40 L60,40 Z" fill="#42A5F5" />
            <rect
              x="40"
              y="40"
              width="120"
              height="1"
              fill="rgba(255,255,255,0.12)"
            />
            <path d="M60,42 L140,42 L125,50 L75,50 Z" fill="#64B5F6" />
            <rect
              x="60"
              y="50"
              width="80"
              height="1"
              fill="rgba(255,255,255,0.1)"
            />
            {/* Bottom (smallest) */}
            <path d="M80,52 L120,52 L110,58 L90,58 Z" fill="#90CAF9" />
          </svg>
        );

      case "table":
      case "datatable":
        return (
          <svg {...commonProps}>
            <rect
              x="15"
              y="10"
              width="170"
              height="40"
              rx="6"
              fill="#F5F9FF"
              stroke="#90CAF9"
              strokeWidth="1.2"
            />
            <rect
              x="15"
              y="10"
              width="170"
              height="10"
              fill="#1E88E5"
              rx="6 6 0 0"
            />
            <rect x="25" y="13" width="30" height="4" fill="white" rx="2" />
            <rect x="70" y="13" width="30" height="4" fill="white" rx="2" />
            <rect x="115" y="13" width="30" height="4" fill="white" rx="2" />
            <rect x="25" y="23" width="30" height="4" fill="#90CAF9" rx="2" />
            <rect x="70" y="23" width="30" height="4" fill="#BBDEFB" rx="2" />
            <rect x="115" y="23" width="30" height="4" fill="#E3F2FD" rx="2" />
            <rect x="25" y="31" width="30" height="4" fill="#90CAF9" rx="2" />
            <rect x="70" y="31" width="30" height="4" fill="#BBDEFB" rx="2" />
            <rect x="115" y="31" width="30" height="4" fill="#E3F2FD" rx="2" />
            <rect x="25" y="39" width="30" height="4" fill="#90CAF9" rx="2" />
            <rect x="70" y="39" width="30" height="4" fill="#BBDEFB" rx="2" />
            <rect x="115" y="39" width="30" height="4" fill="#E3F2FD" rx="2" />
          </svg>
        );

      default:
        return (
          <div className="template-icon-preview">
            <i
              className="ti ti-chart-bar"
              style={{ fontSize: "24px", color: "#1E88E5" }}
            />
          </div>
        );
    }
  };

  const getChartType = (): string => {
    let type = chartType || "";

    //assets
    if (chartType === "assets_category_chart") type = "area";
    if (chartType === "assets_category_pie_chart") type = "donut";

    //leads
    if (chartType === "lead_sources_chart") type = "area";
    if (chartType === "lead_sources_pie_chart") type = "donut";
    if (chartType === "lead_sources_table") type = "table";
    if (chartType === "daily_lead_trends") type = "area";
    if (chartType === "pipeline_stage_distribution") type = "funnel";
    if (chartType === "form_key_stats_chart") type = "area";
    if (chartType === "deal_sources_chart") type = "area";
    if (chartType === "deal_sources_pie_chart") type = "donut";
    if (chartType === "lead_performers_stage_wise_table") type = "table";
    if (chartType === "lead_sources_stage_wise_table") type = "table";
    if (chartType === "deal_performers_stage_wise_table") type = "table";
    if (chartType === "deal_sources_stage_wise_table") type = "table";
    if (chartType === "deal_sources_table") type = "table";

    if (chartType === "daily_deal_trends") type = "area";
    if (chartType === "deal_pipeline_stage_distribution") type = "funnel";

    return type;
  };

  if (componentType === "metric") {
    return renderMetricPreview(valueFormat || "number");
  }

  return renderChartPreview(getChartType());
};

export default ChartPreviewIcon;
